import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiShield, FiLock, FiCheckCircle, FiAlertCircle, FiRefreshCw, FiEdit2, FiInfo } from 'react-icons/fi';
import { phoneOtpApi } from '../api/phoneOtpApi';

const PhoneVerificationBlock = ({ onVerifyStatusChange }) => {
  const [phoneInput, setPhoneInput] = useState('');
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [stagedStatus, setStagedStatus] = useState({ message: '', color: 'text-slate-400' });

  const isDemoMode = import.meta.env.VITE_PHONE_OTP_DEMO_MODE === 'true';
  const hasUrl = !!import.meta.env.VITE_SMS_OTP_GAS_WEB_APP_URL;

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer(p => p - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Helper: Format for display (470 472 915)
  const formatAuMobileDisplay = (input) => {
    let digits = input.replace(/\D/g, '');
    
    // Handle prefixes if they exist in raw input
    if (digits.startsWith('614')) digits = digits.substring(2);
    else if (digits.startsWith('04')) digits = digits.substring(1);
    else if (digits.startsWith('4')) { /* already correct */ }
    else if (digits.length > 0) {
        // If it doesn't start with 4/04/614, we only keep the digits but formatting will be weird
    }

    const match = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,3})$/);
    if (!match) return digits;
    return [match[1], match[2], match[3]].filter(Boolean).join(' ');
  };

  // Helper: Normalize for backend (+614XXXXXXXX)
  const normalizeAuMobile = (input) => {
    let digits = input.replace(/\D/g, '');
    if (digits.startsWith('614')) return '+' + digits;
    if (digits.startsWith('04')) return '+614' + digits.substring(2);
    if (digits.startsWith('4')) return '+614' + digits.substring(1);
    return '';
  };

  const isValidAuMobile = (normalized) => /^\+614\d{8}$/.test(normalized);

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setPhoneInput(formatAuMobileDisplay(val));
    if (error) setError('');
  };

  const updateStage = async (message, color, delay = 600) => {
    setStagedStatus({ message, color });
    await new Promise(r => setTimeout(r, delay));
  };

  const handleSendOtp = async () => {
    const clean = normalizeAuMobile(phoneInput);
    if (!isValidAuMobile(clean)) {
      setError('Enter a valid Australian mobile (e.g. 0425 123 456)');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await updateStage("Checking mobile number...", "text-slate-500");
      await updateStage("Connecting to secure SMS gateway...", "text-blue-500");
      await updateStage("Sending verification code...", "text-amber-500");
      
      const res = await phoneOtpApi.sendSmsOtp(clean);
      
      if (res.ok) {
        await updateStage("Code sent. Please check your phone.", "text-green-600", 800);
        setNormalizedPhone(clean);
        setStep('code');
        setResendTimer(30);
      }
    } catch (err) {
      setError(err.message);
      setStagedStatus({ message: '', color: '' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code sent to your phone');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await updateStage("Checking verification code...", "text-blue-500");
      await updateStage("Confirming with SMS gateway...", "text-amber-500");
      
      const res = await phoneOtpApi.verifySmsOtp(normalizedPhone, code);
      
      if (res.ok) {
        await updateStage("Mobile number verified.", "text-green-600", 800);
        setIsVerified(true);
        setStep('verified');
        onVerifyStatusChange({
          isVerified: true,
          phone: normalizedPhone,
          verifiedAt: res.verifiedAt || new Date().toISOString(),
          verificationType: 'phone'
        });
      }
    } catch (err) {
      setError(err.message);
      setStagedStatus({ message: '', color: '' });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('phone');
    setPhoneInput('');
    setCode('');
    setError('');
    setStagedStatus({ message: '', color: '' });
    setIsVerified(false);
    onVerifyStatusChange({ isVerified: false });
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6 shadow-sm relative overflow-hidden">
      <div className="absolute top-0 right-0 flex">
        {!hasUrl && !isDemoMode && (
          <div className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-bl-lg uppercase tracking-tight">
            Backend Missing
          </div>
        )}
        {isDemoMode && (
          <div className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-bl-lg uppercase tracking-tight">
            Demo Mode
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2 mb-4">
        <SafeIcon icon={FiShield} className="text-blue-600 text-xl" />
        <h3 className="font-semibold text-slate-800 tracking-tight">Mobile Identity Verification</h3>
      </div>

      <AnimatePresence mode="wait">
        {step === 'phone' && (
          <motion.div key="p-step" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Australian Mobile</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-grow flex items-center">
                <div className="absolute left-3 flex items-center gap-2 pointer-events-none">
                  <span className="text-lg" title="Australia">🇦🇺</span>
                  <span className="text-slate-400 font-bold border-r border-slate-200 pr-2">+61</span>
                </div>
                <input 
                  type="tel" 
                  value={phoneInput}
                  onChange={handlePhoneChange}
                  className="pl-20 w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium text-slate-700"
                  placeholder="400 000 000"
                  disabled={loading}
                />
              </div>
              <button 
                type="button" 
                onClick={handleSendOtp}
                disabled={loading || !phoneInput}
                className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-lg font-bold transition-all disabled:opacity-50 min-w-[140px] shadow-sm active:scale-95"
              >
                {loading ? 'Sending...' : 'Send SMS'}
              </button>
            </div>
          </motion.div>
        )}

        {step === 'code' && (
          <motion.div key="c-step" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">6-Digit SMS Code</label>
              <button type="button" onClick={reset} className="text-xs text-blue-600 hover:underline font-bold">Change Number</button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLock} className="text-slate-400" />
                </div>
                <input 
                  type="text" 
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="pl-10 w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-blue-500 outline-none tracking-[0.5em] font-mono text-center font-bold text-lg"
                  placeholder="000000"
                  disabled={loading}
                />
              </div>
              <button 
                type="button" 
                onClick={handleVerifyOtp}
                disabled={loading || code.length !== 6}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold transition-all disabled:opacity-50 min-w-[140px] shadow-sm active:scale-95"
              >
                {loading ? 'Verifying...' : 'Verify SMS'}
              </button>
            </div>
            <div className="mt-3 flex justify-center">
              <button 
                type="button" 
                onClick={handleSendOtp}
                disabled={resendTimer > 0 || loading}
                className="text-xs flex items-center gap-1.5 font-bold text-blue-600 hover:text-blue-700 disabled:text-slate-400 transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
                {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Did not receive? Resend SMS.'}
              </button>
            </div>
          </motion.div>
        )}

        {step === 'verified' && (
          <motion.div key="v-step" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between shadow-inner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xl" />
              </div>
              <div>
                <p className="text-green-800 font-extrabold text-[10px] uppercase tracking-widest">Mobile Identity Verified</p>
                <p className="text-green-600 text-sm font-bold">{normalizedPhone}</p>
              </div>
            </div>
            <button type="button" onClick={reset} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-full transition-all shadow-sm"><SafeIcon icon={FiEdit2} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Staged Progress Messages */}
      <AnimatePresence>
        {loading && stagedStatus.message && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }} 
            animate={{ opacity: 1, height: 'auto' }} 
            exit={{ opacity: 0, height: 0 }}
            className={`flex items-center gap-2 mt-4 text-xs font-bold ${stagedStatus.color} px-1`}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            {stagedStatus.message}
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 mt-4 text-red-600 text-xs bg-red-50 p-2.5 rounded-lg border border-red-100 font-bold shadow-sm">
          <SafeIcon icon={FiAlertCircle} className="shrink-0 text-sm" />
          <span className="leading-tight">{error}</span>
        </motion.div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-200/60 flex items-start gap-2 text-[10px] text-slate-400 italic">
        <SafeIcon icon={FiInfo} className="shrink-0 mt-0.5" />
        <p>SMS verification is sent using the configured verification service name. {isDemoMode ? "In demo mode, use code 123456." : ""}</p>
      </div>
    </div>
  );
};

export default PhoneVerificationBlock;